import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLang } from '@/lib/LanguageContext';
import CountrySelector from '@/components/locale/CountrySelector';

const navLinks = [
{ label: 'Services', to: '/services' },
{ label: 'Prices', to: '/prices' },
{ label: 'Ventures', hash: '#ventures' },
{ label: 'Founder', to: '/founder' },
{ label: 'Blog', to: '/blog' },
{ label: 'About', to: '/about' },
{ label: 'Contact', to: '/contact' }];


export default function Navbar() {
  const { t } = useLang();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    // Read scroll position inside a rAF instead of directly in the scroll event —
    // avoids forcing layout/style recalculation on every scroll tick.
    let frame = null;
    const handleScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        setScrolled(window.scrollY > 50);
        frame = null;
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);



  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? 'bg-background/80 backdrop-blur-xl border-b border-border/50' : 'bg-transparent'}`
      }>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to={isHome ? '#hero' : '/'} className="flex items-center gap-3 group">
              <img src="https://media.base44.com/images/public/69f7328a281ea086a55ced65/e73f4b1ea_Untitled-2.png"
              alt="Jotunheims logo" width="44" height="44" className="w-11 h-11 object-contain" />
              <span className="text-foreground font-inter font-bold text-xl tracking-widest uppercase">
                Jotunheims
              </span>
            </Link>

            {/* Desktop links */}
            <div className="hidden lg:flex items-center gap-7">
              {navLinks.map((link) =>
              <Link
                key={link.label}
                to={link.to || (isHome ? link.hash : `/${link.hash}`)}
                className="text-muted-foreground text-sm font-medium tracking-wider uppercase hover:text-primary transition-colors duration-300 relative group flex items-center gap-1.5">
                
                  {t(link.label)}
                  {link.highlight && <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-primary group-hover:w-full transition-all duration-300" />
                </Link>
              )}
              <CountrySelector />
              <Link to="/contact" className="px-5 py-2.5 bg-primary text-primary-foreground font-semibold text-xs tracking-wider uppercase rounded-lg hover:bg-primary/90 transition-all">
                {t('Get a Quote')}
              </Link>
            </div>


            {/* Mobile toggle */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? t('Close menu') : t('Open menu')}
              aria-expanded={isOpen}
              className="lg:hidden w-10 h-10 flex items-center justify-center border border-border rounded-lg hover:border-primary/50 transition-colors">
              
              {isOpen ? <X className="w-5 h-5 text-primary" /> : <Menu className="w-5 h-5 text-foreground" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen &&
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 bg-background/95 backdrop-blur-2xl flex flex-col items-center justify-center gap-8">
          
            {navLinks.map((link, i) =>
          <motion.div
            key={link.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}>
            <Link
              to={link.to || (isHome ? link.hash : `/${link.hash}`)}
              onClick={() => setIsOpen(false)}
              className="text-3xl font-inter font-bold tracking-widest uppercase text-foreground hover:text-primary transition-colors">
              {t(link.label)}
            </Link>
          </motion.div>
          )}
            <Link to="/contact" onClick={() => setIsOpen(false)} className="px-8 py-3 bg-primary text-primary-foreground font-semibold text-sm tracking-wider uppercase rounded-lg">
              {t('Get a Quote')}
            </Link>
            <CountrySelector variant="mobile" onNavigate={() => setIsOpen(false)} />
          </motion.div>
        }
      </AnimatePresence>
    </>);

}